import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Eye, EyeOff, Loader2, ShieldCheck, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

// HaulLogix Logo Component - Shield Logo
const HaulLogixLogo = () => (
  <svg width="48" height="54" viewBox="0 0 100 110" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="loginShieldBorder" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#1E3A5F" />
        <stop offset="40%" stopColor="#3B82F6" />
        <stop offset="70%" stopColor="#60A5FA" />
        <stop offset="100%" stopColor="#94A3B8" />
      </linearGradient>
      <linearGradient id="loginInnerShield" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#1E3A5F" />
        <stop offset="100%" stopColor="#0F172A" />
      </linearGradient>
      <linearGradient id="loginBlueBar" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#38BDF8" />
        <stop offset="30%" stopColor="#3B82F6" />
        <stop offset="100%" stopColor="#1D4ED8" />
      </linearGradient>
      <linearGradient id="loginGreenBar" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#4ADE80" />
        <stop offset="30%" stopColor="#22C55E" />
        <stop offset="100%" stopColor="#15803D" />
      </linearGradient>
    </defs>
    <path d="M50 5L10 22V55C10 82 50 105 50 105C50 105 90 82 90 55V22L50 5Z" fill="url(#loginShieldBorder)"/>
    <path d="M50 12L16 27V55C16 78 50 98 50 98C50 98 84 78 84 55V27L50 12Z" fill="url(#loginInnerShield)"/>
    <path d="M26 30 L26 78 L40 72 L40 36 Z" fill="url(#loginBlueBar)" />
    <path d="M60 36 L60 72 L74 78 L74 30 Z" fill="url(#loginGreenBar)" />
    <rect x="36" y="48" width="28" height="12" fill="#FFFFFF" opacity="0.95" />
  </svg>
);

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, requires2FA, verify2FA, cancel2FA, pendingUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [twoFACode, setTwoFACode] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await login(formData.email, formData.password);
      if (result.requires_2fa) {
        toast.info('Please enter your 2FA code');
      } else {
        toast.success('Welcome back!');
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await verify2FA(twoFACode);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid 2FA code');
    } finally {
      setLoading(false);
    }
  };

  const handleBack2FA = () => {
    cancel2FA();
    setTwoFACode('');
  };

  // 2FA Verification Screen
  if (requires2FA) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4 pt-16">
        <div className="w-full max-w-md animate-fade-in">
          {/* Logo */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-3">
              <HaulLogixLogo />
              <span className="text-2xl font-bold tracking-wider uppercase">
                <span className="text-[#0D1B2A]">HAUL</span><span className="text-[#22C55E]">LOGIX</span>
              </span>
            </Link>
          </div>

          <Card className="border-gray-200 bg-white shadow-xl">
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <div className="p-3 rounded-full bg-[#3B82F6]/10">
                  <ShieldCheck className="w-8 h-8 text-[#3B82F6]" />
                </div>
              </div>
              <CardTitle className="text-2xl font-bold uppercase font-['Poppins'] tracking-wide text-gray-900">
                Two-Factor Authentication
              </CardTitle>
              <CardDescription className="text-gray-600">
                Enter the 6-digit code from your authenticator app
                {pendingUser?.email && (
                  <span className="block mt-1 text-[#3B82F6]">{pendingUser.email}</span>
                )}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handle2FASubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="2fa-code" className="text-gray-700 font-medium">Authentication Code</Label>
                  <Input
                    id="2fa-code"
                    type="text"
                    placeholder="000000"
                    value={twoFACode}
                    onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    required
                    maxLength={6}
                    data-testid="2fa-code-input"
                    className="h-14 bg-gray-50 border-gray-200 focus:border-[#3B82F6] text-center text-2xl tracking-[0.5em] font-mono text-gray-900"
                    autoFocus
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 bg-gradient-to-r from-[#4ADE80] to-[#22C55E] hover:from-[#86EFAC] hover:to-[#4ADE80] text-black uppercase tracking-wider font-semibold"
                  style={{boxShadow: '0 4px 16px rgba(74, 222, 128, 0.4)'}}
                  disabled={loading || twoFACode.length !== 6}
                  data-testid="2fa-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    'Verify Code'
                  )}
                </Button>

                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleBack2FA}
                  className="w-full text-gray-400 hover:text-white"
                  data-testid="2fa-back-btn"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Login
                </Button>
              </form>

              <div className="mt-6 p-4 rounded-lg bg-[#0A1628] border border-[#3B82F6]/10">
                <p className="text-sm text-gray-400 text-center">
                  You can also use one of your backup codes if you don't have access to your authenticator app.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4 pt-16">
      <div className="w-full max-w-md animate-fade-in">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3">
            <HaulLogixLogo />
            <span className="text-2xl font-bold tracking-wider uppercase">
              <span className="text-[#0D1B2A]">HAUL</span><span className="text-[#22C55E]">LOGIX</span>
            </span>
          </Link>
        </div>

        <Card className="border-gray-200 bg-white shadow-xl">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-bold uppercase font-['Poppins'] tracking-wide text-gray-900">
              Welcome Back
            </CardTitle>
            <CardDescription className="text-gray-600">
              Sign in to access your dashboard
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-gray-700 font-medium">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="login-email-input"
                  className="h-12 bg-gray-50 border-gray-200 focus:border-[#3B82F6] text-gray-900"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-gray-700 font-medium">Password</Label>
                  <Link 
                    to="/forgot-password" 
                    className="text-sm text-[#3B82F6] hover:text-[#2563EB] transition-colors"
                    data-testid="forgot-password-link"
                  >
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    data-testid="login-password-input"
                    className="h-12 bg-gray-50 border-gray-200 focus:border-[#3B82F6] pr-10 text-gray-900"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-12 bg-gradient-to-r from-[#4ADE80] to-[#22C55E] hover:from-[#86EFAC] hover:to-[#4ADE80] text-black uppercase tracking-wider font-semibold"
                style={{boxShadow: '0 4px 16px rgba(74, 222, 128, 0.4)'}}
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Signing In...
                  </>
                ) : (
                  'Sign In'
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-gray-600 text-sm">
                Don't have an account?{' '}
                <Link to="/register" className="text-[#22C55E] hover:text-[#16A34A] font-medium hover:underline" data-testid="register-link">
                  Create one
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LoginPage;
